import { useRuntimeConfig } from '#imports'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { defineStore } from 'pinia'

import type { SellerData } from '../../../api/seller/types'

interface State {
  sellerInfo: SellerData | null
}

export const useSellerInfoStore = defineStore('sellerInfo', {
  actions: {
    clear() {
      // Doc: https://pinia.vuejs.org/core-concepts/state.html#resetting-the-state
      this.$reset()
    },

    /**
     * Retrieves the Seller Info associated with the current authenticated User.
     */
    async fetchSellerInfo() {
      const { FF_BO_SELLER_USE_NEW_SELLER_INFO_FETCH_ENDPOINT } =
        useRuntimeConfig().public

      const $httpFetch = useHttpFetch()

      if (FF_BO_SELLER_USE_NEW_SELLER_INFO_FETCH_ENDPOINT) {
        this.sellerInfo = await $httpFetch(
          '/api/seller-experience/seller/information',
        )
      } else {
        this.sellerInfo = await $httpFetch('/bm/merchant')
      }
    },
  },
  getters: {
    activeMarkets: (state) =>
      (state.sellerInfo?.activeMarkets as MarketCountryCode[]) || [],
    canOpenNewMarkets: (state) => state.sellerInfo?.canOpenNewMarkets || false,
    canSeeChineseLanguage: (state) =>
      state.sellerInfo?.boInChineseEnabled || false,
    companyName: (state) => state.sellerInfo?.company,
    countryOfOrigin: (state) => state.sellerInfo?.countryOfOrigin,
    defaultVat: (state) =>
      state.sellerInfo?.vat.isConcernedByVatCollection
        ? state.sellerInfo.vat
        : null,
    email: (state) => state.sellerInfo?.email,
    hasFetchSellerInfo: (state) => Boolean(state.sellerInfo),
    hasFilledNewKybForm: (state) =>
      Boolean(state.sellerInfo?.hasFilledNewKybForm),
    hasKybVerified: (state) => Boolean(state.sellerInfo?.hasKybVerified),
    hasOnlyOneActiveMarket: (state) =>
      state.sellerInfo?.activeMarkets.length === 1,
    hasOnlyOneMarket: (state) => {
      // to get all markets, we need to add the amount of potentialMarkets and activeMarkets
      // for example, this is a way to share to the store a boolean that help us
      // know if we should display components related to markets
      return (
        state.sellerInfo &&
        state.sellerInfo.potentialMarkets.length +
          state.sellerInfo.activeMarkets.length ===
          1
      )
    },
    id: (state) => state.sellerInfo?.id,
    isAdmin: (state) => Boolean(state.sellerInfo?.sellerUser.isAdmin),
    isNewBatteryListingsEnabled: (state) =>
      Boolean(state.sellerInfo?.isNewBatteryListingsEnabled),
    isBuybackEnabled: (state) => state.sellerInfo?.isBuybackEnabled,
    isConcernedByVatCollection: (state) =>
      Boolean(state.sellerInfo?.vat.isConcernedByVatCollection),
    isImpersonated: (state) => Boolean(state.sellerInfo?.isImpersonated),
    isPremiumGradeListingsEnabled: (state) =>
      Boolean(state.sellerInfo?.isPremiumGradeListingsEnabled),
    onboardingCompleted: (state) =>
      Boolean(state.sellerInfo?.onboardingCompleted),
    permissions: (state) =>
      (state.sellerInfo?.sellerUser.permissions || []) as string[],
    potentialMarkets: (state) => state.sellerInfo?.potentialMarkets || [],
    publicId: (state) => state.sellerInfo?.publicId,
    referenceMarket: (state) =>
      (state.sellerInfo?.referenceMarket as MarketCountryCode) || '',
  },
  state: (): State => ({
    sellerInfo: null,
  }),
})
